/**
 * @file src/lib/functions/splitCaseItems.ts
 * 
 * This function takes an array of items and splits any items that have a case discount into individual case and remainder items.
 * 
 * @returns {OrderPayloadItemType[]} An array of items with case items split into individual case and remainder items.  
 */

import type { OrderPayloadItemType } from '@/components/cart/cartItems';

export function splitCaseItems(items: OrderPayloadItemType[]): OrderPayloadItemType[] {
  const expandedItems: OrderPayloadItemType[] = [];

  for (const item of items) {
    const itemPricing = item.pricing?.itemPricing;
    const appliedPricing = item.pricing?.appliedPricing;

    if (!itemPricing || !appliedPricing) {
      expandedItems.push(item);
      continue;
    }

    const caseQuantity = itemPricing.caseQuantity ?? 0;
    const casePrice = itemPricing.casePrice;
    const unitPrice = itemPricing.unitPrice;
    const unitPriceBeforeDiscount = itemPricing.unitPriceBeforeDiscount;
    const discountType = appliedPricing.discountType;

    if (
      discountType === 'case' &&
      caseQuantity > 1 &&
      item.quantity >= caseQuantity &&
      casePrice != null &&
      unitPrice != null
    ) {
      const fullCases = Math.floor(item.quantity / caseQuantity);
      const remainder = item.quantity % caseQuantity;

      const caseUnitPrice = casePrice;
      const caseUnitPriceBeforeDiscount = casePrice * caseQuantity;
      const caseSubtotal = casePrice * fullCases;

      expandedItems.push({
        ...item,
        quantity: caseQuantity * fullCases,
        caseQuantity: fullCases,
        totalItemQuantity: item.quantity,
        pricing: {
          ...item.pricing,
          itemPricing: {
            ...item.pricing.itemPricing,
            unitPrice: caseUnitPrice,
            unitPriceBeforeDiscount: caseUnitPriceBeforeDiscount ? caseUnitPriceBeforeDiscount : unitPrice,
          },
          appliedPricing: {
            ...item.pricing.appliedPricing,
            discountType: 'case',
            subtotal: caseSubtotal,
          }
        },
        label: 'Case',
        isSplit: true,
      });

      if (remainder > 0) {
        const regularUnitPrice = unitPriceBeforeDiscount ?? unitPrice;
        const remainderSubtotal = Math.round(regularUnitPrice * remainder);

        expandedItems.push({
          ...item,
          quantity: remainder,
          totalItemQuantity: item.quantity,
          pricing: {
            ...item.pricing,
            itemPricing: {
              ...item.pricing.itemPricing,
              unitPrice: regularUnitPrice,
              unitPriceBeforeDiscount: undefined,
            },
            appliedPricing: {
              ...item.pricing.appliedPricing,
              discountType: undefined,
              subtotal: remainderSubtotal,
              total: remainderSubtotal,
            }
          },
          label: 'Individual',
          isSplit: true,
        });
      }
    } else {
      expandedItems.push(item);
    }
  }

  return expandedItems;
}
